import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useSetAtom } from 'jotai';
import React, { useCallback } from 'react';

import {
  FollowupMessageEventPatientDetailFragment,
  FollowupQuestionnaireSheetEventOnPatientDetailDialogFragment,
  GuestPatientMedicationFollowupCommentFragment,
  PatientMedicationFollowupCommentFragment,
  TracingReportPatientDetailFragment,
} from '~/graphql/generated/graphql';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { answerState } from '~/state/partials/patient_detail_dialog/atoms';

import { FollowupMessageEvent } from './Events/FollowupMessageEvent';
import { GuestPatientMedicationFollowupComment } from './Events/GuestPatientMedicationFollowupComment';
import { MessageEvent } from './Events/MessageEvent';
import { PatientMedicationFollowupComment } from './Events/PatientMedicationFollowupComment';
import { TracingreportEvent } from './Events/TracingreportEvent';
import { QuestionnaireSheet } from './QuestionnaireSheet';

type Props = {
  event:
    | GuestPatientMedicationFollowupCommentFragment
    | PatientMedicationFollowupCommentFragment
    | FollowupMessageEventPatientDetailFragment
    | FollowupQuestionnaireSheetEventOnPatientDetailDialogFragment
    | TracingReportPatientDetailFragment;
  patientType: 'DirectVisitor' | 'Patient' | 'WebVisitor' | 'LineVisitor' | undefined;
  selectedSheetId: string | null;
};

const Root = styled('div')(({ theme }) =>
  css({
    paddingTop: theme.space.l,
    paddingLeft: theme.space.l,
    paddingRight: theme.space.l,
    backgroundColor: theme.colors.colorPallete.grey04,
  }),
);

const Event: React.FC<Props> = ({ event, patientType, selectedSheetId }) => {
  const { verifyRole } = usePractitionerRole();
  const isPharmacist = verifyRole('pharmacist');
  const setAnswerState = useSetAtom(answerState);

  const handleClickSheet = useCallback(
    (answer: string) => {
      setAnswerState((_state) => ({ ..._state, answer }));
    },
    [setAnswerState],
  );
  return (
    <Root key={event.id}>
      {event.__typename === 'MedicationFollowupQuestionnaireSheet' ? (
        <QuestionnaireSheet
          selected={event.id === selectedSheetId}
          sheet={event}
          onClick={handleClickSheet}
          readonly={!isPharmacist}
        />
      ) : event.__typename === 'MedicationFollowupMessage' ? (
        event.medicationFollowupQuestionnaireRevision ? (
          <FollowupMessageEvent followupMessage={event} readonly={!isPharmacist} />
        ) : (
          <MessageEvent message={event} readonly={!isPharmacist} />
        )
      ) : event.__typename === 'PatientMedicationFollowupComment' ? (
        <PatientMedicationFollowupComment comment={event} />
      ) : event.__typename === 'GuestPatientMedicationFollowupComment' ? (
        patientType === 'DirectVisitor' ||
        patientType === 'WebVisitor' ||
        patientType === 'LineVisitor' ? (
          <GuestPatientMedicationFollowupComment patientType={patientType} comment={event} />
        ) : null
      ) : event.__typename === 'TracingReport' ? (
        <TracingreportEvent tracingreport={event} />
      ) : null}
    </Root>
  );
};

export default Event;
